import deepEqual from 'fast-deep-equal';
import { Box3, Matrix4, Object3D, Sphere } from 'three';

import { Schema, SchemaImpl, SchemaVariables } from '../schema/index.js';
import { uuid } from '../utils/index.js';

import { FComposition, FCompositionNode } from './types.js';

const mat4 = new Matrix4();

/**
 * Abstract base class for manage furniture compositions.
 *
 * Given a schema definition, this class provides the basic structure and methods for managing compositions.
 */
export abstract class FCompositionImpl<TSchema extends Schema, TVariables extends SchemaVariables>
  extends SchemaImpl<TSchema, TVariables>
  implements FComposition<TSchema, TVariables>
{
  uuid = '';
  /**
   * Bounding box for the {@link THREE.BufferGeometry | BufferGeometry}, which can be calculated with {@link computeBoundingBox | .computeBoundingBox()}.
   * @remarks Bounding boxes aren't computed by default. They need to be explicitly computed, otherwise they are `null`.
   * @defaultValue `null`
   */
  boundingBox: Box3 | null = null;

  /**
   * Computes bounding box of the geometry, updating {@link boundingBox | .boundingBox} attribute.
   * @remarks Bounding boxes aren't computed by default. They need to be explicitly computed, otherwise they are `null`.
   */
  computeBoundingBox(): void {
    const nodeTypes = this.getNodeTypes();
    const objects = this.getObjects();
    if (nodeTypes.length !== objects.length) {
      throw new Error(
        `Number of node types (${nodeTypes.length}) and objects (${objects.length}) must match`
      );
    }
    const nodeObjectMap = Object.fromEntries(nodeTypes.map((type, idx) => [type, objects[idx]]));

    if (!this.boundingBox) {
      this.boundingBox = new Box3().makeEmpty();
    }

    this.traverseNodes((node, parent) => {
      const object = nodeObjectMap[node.id];
      if (!object) {
        throw new Error(`Object not found for node type ${node.id}`);
      }
      const bbox = new Box3().setFromObject(object);
      this.boundingBox!.union(
        bbox
          .applyMatrix4(mat4.makeRotationY((parent?.rotation ?? 0) + node.rotation))
          .applyMatrix4(
            mat4.makeTranslation(
              (parent?.position[0] ?? 0) + node.position[0],
              0,
              (parent?.position[1] ?? 0) + node.position[1]
            )
          )
      );
    });
  }

  /**
   * Bounding sphere for the {@link THREE.BufferGeometry | BufferGeometry}, which can be calculated with {@link computeBoundingSphere | .computeBoundingSphere()}.
   * @remarks bounding spheres aren't computed by default. They need to be explicitly computed, otherwise they are `null`.
   * @defaultValue `null`
   */
  boundingSphere: Sphere | null = null;

  /**
   * Computes bounding sphere of the geometry, updating {@link boundingSphere | .boundingSphere} attribute.
   * @remarks bounding spheres aren't computed by default. They need to be explicitly computed, otherwise they are `null`.
   */
  computeBoundingSphere() {
    if (!this.boundingBox) {
      this.computeBoundingBox();
    }

    if (!this.boundingSphere) {
      this.boundingSphere = new Sphere();
    }

    this.boundingBox!.getBoundingSphere(this.boundingSphere);
  }

  /**
   * The objects for the arrangement
   */
  objects: Object3D[] = [];

  /**
   * Method to set the objects.
   * This will mark the schema and attributes as needing an update if the objects have changed.
   */
  setObjects(objects: Object3D[]) {
    if (!deepEqual(this.objects, objects)) {
      this.objects = objects;
      this.boundingBox = null;
      this.boundingSphere = null;
      this.updateObjectMeasurements();
      return true;
    }

    return false;
  }

  /**
   * Get the objects for the arrangement
   */
  getObjects(): Object3D[] {
    return this.objects;
  }

  protected updateObjectMeasurements() {
    throw new Error('FComposition.updateObjectMeasurements Method not implemented.');
  }

  /**
   * Get the node types for the given objects and attributes
   */
  getNodeTypes(): number[] {
    throw new Error('FComposition.getNodeTypes Method not implemented.');
  }

  /**
   * Get the nodes for the given objects and attributes
   */
  getNodes(): FCompositionNode[] {
    throw new Error('FComposition.getNodes Method not implemented.');
  }

  traverseNodes(fn: (node: FCompositionNode, parent: FCompositionNode | null) => void) {
    const nodes = this.getNodes();

    const traverse = (node: FCompositionNode, parent: FCompositionNode | null) => {
      fn(node, parent);
      if (node.children) {
        node.children.forEach((n) => traverse(n, node));
      }
    };

    nodes.forEach((n) => traverse(n, null));
  }

  getSafeIterations(count: number): number {
    if (!Number.isFinite(count) || Number.isNaN(count) || !Number.isInteger(count)) {
      console.warn('Invalid count');
      return 1;
    }
    return count;
  }

  /**
   * Get the node uuid for the given type and index
   */
  getNodeUuid(id: number, index: number = 0) {
    return uuid(`${this.uuid}-${id}-${index}`);
  }
}
